import React, { useState, useEffect } from "react";
import "./GamersCockpit.scss";

/**
 * GAMERS' COCKPIT - Where Every Login Feels Like Victory
 * An immersive command center that makes tracking feel like playing
 */
const GamersCockpit: React.FC = () => {
    const [currentView, setCurrentView] = useState<"overview" | "quests" | "stash">("overview");
    const [streakCount, setStreakCount] = useState(7);
    const [xpGained, setXpGained] = useState(0);
    const [liveEvents, setLiveEvents] = useState<Array<{ id: string, text: string, type: string }>>([]);

    // Simulate live feed updates
    useEffect(() => {
        const eventTypes = [
            { text: "🎯 Headshot Master completed!", type: "quest" },
            { text: "⚡ +250 XP earned", type: "xp" },
            { text: "🎁 Snapple Lootbox unlocked!", type: "loot" },
            { text: "🔥 7 day streak bonus!", type: "streak" },
            { text: "⚔️ Guild ranked up to #5", type: "guild" },
        ];

        const interval = setInterval(() => {
            const newEvent = eventTypes[Math.floor(Math.random() * eventTypes.length)];
            setLiveEvents(prev => [{
                id: Date.now().toString(),
                ...newEvent,
            }, ...prev].slice(0, 5));

            if (newEvent.type === "xp") {
                setXpGained(prev => prev + 250);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="gamers-cockpit">
            {/* HEADER BAR - Always Visible */}
            <div className="cockpit-header">
                <div className="player-badge">
                    <div className="avatar-frame">
                        <img src="/placeholder-avatar.jpg" alt="Player" className="avatar" />
                        <div className="level-orb">42</div>
                        <div className="streak-flame" data-days={streakCount}>
                            <span className="flame-icon">🔥</span>
                            <span className="streak-number">{streakCount}</span>
                        </div>
                    </div>

                    <div className="player-info">
                        <div className="player-name">DEATHKILLER99</div>
                        <div className="player-tier">
                            <span className="tier-icon">⭐</span>
                            <span className="tier-name">OPERATOR</span>
                        </div>
                        <div className="xp-progress">
                            <div className="xp-bar">
                                <div className="xp-fill" style={{width: "67%"}}>
                                    <div className="xp-glow" />
                                </div>
                                <div className="xp-text">14,280 / 20,000 XP</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="resource-meters">
                    <div className="fuel-meter">
                        <div className="meter-icon">⚡</div>
                        <div className="meter-value">2,450</div>
                        <div className="meter-label">FUEL</div>
                    </div>
                    <div className="loot-meter">
                        <div className="meter-icon">🎁</div>
                        <div className="meter-value">3</div>
                        <div className="meter-label">UNOPENED</div>
                    </div>
                </div>

                <div className="guild-badge">
                    <div className="guild-icon">🛡️</div>
                    <div className="guild-info">
                        <div className="guild-name">APEX PREDATORS</div>
                        <div className="guild-rank">#7 GLOBAL</div>
                    </div>
                    <div className="contribution-spark">+1.2K</div>
                </div>
            </div>

            {/* MAIN COCKPIT AREA */}
            <div className="cockpit-body">

                {/* LEFT PANEL: Journey Map */}
                <div className="journey-panel">
                    <div className="panel-header">
                        <h2>ACTIVE JOURNEYS</h2>
                        <div className="journey-selector">
                            <button className="journey-tab active">SNAPPLE SNIPER</button>
                            <button className="journey-tab">COD RZR RUN</button>
                            <button className="journey-tab notification">DAILY GRIND</button>
                        </div>
                    </div>

                    <div className="journey-map">
                        {/* Journey Quest Steps */}
                        <div className="journey-step completed">
                            <div className="step-connector" />
                            <div className="step-node">
                                <div className="node-icon">✅</div>
                            </div>
                            <div className="step-content">
                                <h4>INITIATION</h4>
                                <p>Get 5 headshots in any game</p>
                                <div className="reward-pills">
                                    <span className="reward-pill">+100 XP</span>
                                </div>
                            </div>
                        </div>

                        <div className="journey-step completed">
                            <div className="step-connector" />
                            <div className="step-node">
                                <div className="node-icon">✅</div>
                            </div>
                            <div className="step-content">
                                <h4>BRAND SYNC</h4>
                                <p>Scan Snapple QR at store</p>
                                <div className="reward-pills">
                                    <span className="reward-pill">+250 XP</span>
                                    <span className="reward-pill special">🎁 LOOTBOX</span>
                                </div>
                            </div>
                        </div>

                        <div className="journey-step active">
                            <div className="step-connector pulse" />
                            <div className="step-node">
                                <div className="node-icon">⚔️</div>
                                <div className="node-progress">
                                    <svg viewBox="0 0 36 36">
                                        <path
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            fill="none"
                                            stroke="#444"
                                            strokeWidth="3"
                                        />
                                        <path
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            fill="none"
                                            stroke="url(#gradient)"
                                            strokeWidth="3"
                                            strokeDasharray="60, 100"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div className="step-content">
                                <h4>SNIPER ELITE</h4>
                                <p>Get 15 sniper kills (9/15)</p>
                                <div className="progress-micro">
                                    <div className="progress-fill" style={{width: "60%"}} />
                                </div>
                                <div className="reward-pills">
                                    <span className="reward-pill">+500 XP</span>
                                    <span className="reward-pill">+100 FUEL</span>
                                </div>
                            </div>
                        </div>

                        <div className="journey-step locked">
                            <div className="step-connector" />
                            <div className="step-node">
                                <div className="node-icon">🔒</div>
                            </div>
                            <div className="step-content locked-content">
                                <h4>FINAL CHALLENGE</h4>
                                <p>Unlock previous steps first</p>
                                <div className="reward-pills">
                                    <span className="reward-pill epic">🏆 EPIC REWARD</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="journey-rewards">
                        <h3>JOURNEY COMPLETION</h3>
                        <div className="grand-prize">
                            <div className="prize-glow" />
                            <img src="/images/rewards/snapple-gaming-chair.png" alt="Grand Prize" />
                            <div className="prize-label">SNAPPLE GAMING CHAIR</div>
                        </div>
                    </div>
                </div>

                {/* CENTER PANEL: Quest Cards */}
                <div className="quest-central">
                    <div className="daily-quests">
                        <h3>DAILY MISSIONS</h3>
                        <div className="quest-grid">
                            <div className="quest-card daily">
                                <div className="quest-icon">🎯</div>
                                <div className="quest-info">
                                    <h4>SHARPSHOOTER</h4>
                                    <p>10 headshots</p>
                                    <div className="quest-progress">
                                        <div className="progress-bar">
                                            <div className="progress-fill" style={{width: "70%"}} />
                                        </div>
                                        <span className="progress-text">7/10</span>
                                    </div>
                                </div>
                                <div className="quest-reward">
                                    <span className="reward-amount">+150</span>
                                    <span className="reward-type">XP</span>
                                </div>
                            </div>

                            <div className="quest-card weekly">
                                <div className="quest-timer">2D 14H</div>
                                <div className="quest-icon">⚔️</div>
                                <div className="quest-info">
                                    <h4>SQUAD WIPER</h4>
                                    <p>Eliminate 5 full squads</p>
                                    <div className="quest-progress">
                                        <div className="progress-bar">
                                            <div className="progress-fill" style={{width: "40%"}} />
                                        </div>
                                        <span className="progress-text">2/5</span>
                                    </div>
                                </div>
                                <div className="quest-reward">
                                    <span className="reward-amount">+500</span>
                                    <span className="reward-type">XP</span>
                                </div>
                            </div>

                            <div className="quest-card guild">
                                <div className="guild-marker">GUILD QUEST</div>
                                <div className="quest-icon">🛡️</div>
                                <div className="quest-info">
                                    <h4>UNITED FRONT</h4>
                                    <p>Guild: 500 total kills</p>
                                    <div className="quest-progress">
                                        <div className="progress-bar guild-progress">
                                            <div className="progress-fill" style={{width: "82%"}} />
                                        </div>
                                        <span className="progress-text">412/500</span>
                                    </div>
                                </div>
                                <div className="quest-reward">
                                    <span className="reward-amount">+1K</span>
                                    <span className="reward-type">GUILD XP</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Featured Brand Campaign */}
                    <div className="brand-spotlight">
                        <div className="spotlight-header">
                            <img src="/images/brands/cod-logo.png" alt="Call of Duty" className="brand-logo" />
                            <div className="spotlight-timer">
                                <span className="timer-icon">⏰</span>
                                <span className="timer-text">ENDS IN 48H</span>
                            </div>
                        </div>
                        <div className="spotlight-content">
                            <h2>RZR RAMPAGE WEEKEND</h2>
                            <p>Complete challenges for exclusive RZR gear</p>
                            <div className="spotlight-rewards">
                                <div className="reward-track">
                                    <div className="track-item completed">
                                        <div className="item-icon">✅</div>
                                        <span>RZR Decal</span>
                                    </div>
                                    <div className="track-item active">
                                        <div className="item-icon">🎮</div>
                                        <span>RZR Controller Skin</span>
                                    </div>
                                    <div className="track-item">
                                        <div className="item-icon">🏆</div>
                                        <span>RZR Gaming Chair</span>
                                    </div>
                                </div>
                            </div>
                            <button className="spotlight-cta">
                                <span className="cta-text">JOIN CHALLENGE</span>
                                <div className="cta-shine" />
                            </button>
                        </div>
                    </div>

                    {/* Live Activity Feed */}
                    <div className="live-feed">
                        <div className="feed-header">
                            <div className="feed-pulse" />
                            <h3>LIVE FEED</h3>
                        </div>
                        <div className="feed-stream">
                            {liveEvents.map(event =>
                                <div key={event.id} className={`feed-event feed-event--${event.type}`}>
                                    <span className="event-text">{event.text}</span>
                                    <span className="event-time">now</span>
                                </div>)}
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL: Stash & Actions */}
                <div className="command-panel">
                    {/* Quick Actions */}
                    <div className="quick-actions">
                        <button className="action-button primary">
                            <span className="button-icon">🎮</span>
                            <span className="button-text">FIND MATCH</span>
                            <div className="button-glow" />
                        </button>
                        <button className="action-button rewards">
                            <span className="button-icon">🎁</span>
                            <span className="button-text">OPEN LOOT</span>
                            <div className="button-badge">3</div>
                        </button>
                    </div>

                    {/* My Stash Preview */}
                    <div className="stash-preview">
                        <div className="stash-header">
                            <h3>MY STASH</h3>
                            <button className="view-all">VIEW ALL</button>
                        </div>
                        <div className="stash-grid">
                            <div className="stash-item legendary">
                                <div className="item-rarity">LEGENDARY</div>
                                <div className="item-icon">🎁</div>
                                <div className="item-count">1</div>
                            </div>
                            <div className="stash-item rare">
                                <div className="item-rarity">RARE</div>
                                <div className="item-icon">⚡</div>
                                <div className="item-label">2X XP</div>
                            </div>
                            <div className="stash-item epic">
                                <div className="item-rarity">EPIC</div>
                                <div className="item-icon">💎</div>
                                <div className="item-label">NFT</div>
                            </div>
                            <div className="stash-item common">
                                <div className="item-icon">🎟️</div>
                                <div className="item-label">FUEL x100</div>
                            </div>
                        </div>
                    </div>

                    {/* Recommended For You */}
                    <div className="recommendations">
                        <h3>RECOMMENDED</h3>
                        <div className="reco-card">
                            <div className="reco-tag">COMPETITOR TYPE</div>
                            <h4>RANKED GRINDER</h4>
                            <p>Climb 5 ranks this week</p>
                            <div className="reco-reward">
                                <span>+1000 XP</span>
                            </div>
                        </div>
                    </div>

                    {/* Platform Connections */}
                    <div className="platforms">
                        <div className="platform-icon xbox active" />
                        <div className="platform-icon steam active" />
                        <div className="platform-icon playstation" />
                        <div className="platform-icon epic" />
                    </div>
                </div>
            </div>

            {/* Gradient Definitions */}
            <svg width="0" height="0">
                <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#7859E6" />
                        <stop offset="100%" stopColor="#FF4238" />
                    </linearGradient>
                </defs>
            </svg>
        </div>
    );
};

export default GamersCockpit;